import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Check, ExternalLink } from "lucide-react";
import { SectionHeading } from "../home/CtaButtons";
import { imageUrl, trackEvent } from "../../lib/api";
import { quoteHref } from "../../data/homeContent";
import { systemSpecs, useProfileSystems } from "../../lib/catalog";

const KEY_SPECS = ["Bouwdiepte", "Kameropbouw", "Uf-waarde profiel", "Uw-waarde raam", "Glasdikte"];

export const SystemBadges = ({ s }) => (
  <ul className="flex flex-wrap gap-1.5">
    {s.hr_plus_plus && <li className="chip"><Check className="h-3 w-3 text-maas" aria-hidden="true" />Geschikt voor HR++ glas</li>}
    {s.hr_triple && <li className="chip"><Check className="h-3 w-3 text-maas" aria-hidden="true" />Geschikt voor HR+++ glas</li>}
  </ul>
);

const SystemCard = ({ s, onderwerp, from }) => {
  const specs = systemSpecs(s).filter(([k]) => KEY_SPECS.includes(k));
  return (
    <li className="card flex flex-col overflow-hidden" data-testid={`profile-system-card-${s.slug}`}>
      {s.images?.[0] && <img src={imageUrl(s.images[0])} alt={`Profielsysteem ${s.name}`} loading="lazy" className="aspect-[16/9] w-full object-cover" />}
      <div className="flex flex-1 flex-col p-6">
        <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-gold">{s.manufacturer || "Profielsysteem"} · {s.material}</p>
        <h3 className="h3 mt-2">{s.name}</h3>
        {s.summary && <p className="mt-2 text-[15px] leading-relaxed text-slate-600">{s.summary}</p>}
        {specs.length > 0 && (
          <dl className="mt-5 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
            {specs.map(([k, v]) => <div key={k}><dt className="text-[11.5px] font-semibold uppercase tracking-[0.12em] text-slate-500">{k}</dt><dd className="mt-0.5 text-[14.5px] text-ink">{v}</dd></div>)}
          </dl>
        )}
        <div className="mt-5"><SystemBadges s={s} /></div>
        {s.variants?.length > 0 && <p className="mt-4 text-[13.5px] text-slate-500">Varianten: {s.variants.map((v) => v.name).join(", ")}</p>}
        <div className="mt-auto flex flex-wrap items-center gap-x-5 gap-y-3 border-t border-slate-100 pt-5">
          <Link to={quoteHref(onderwerp, { systeem: s.slug })} state={{ from }} data-testid={`profile-system-quote-${s.slug}`}
            onClick={() => trackEvent("cta_quote_click", { location: "profile-system", system: s.slug })} className="btn-primary !min-h-[44px] text-[14px]">
            Prijsindicatie met {s.name} <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
          {s.seo_page && <Link to={`/profielsystemen/${s.slug}`} className="text-[14px] font-semibold text-ink hover:text-maas" data-testid={`profile-system-link-${s.slug}`}>Meer over {s.name}</Link>}
          {s.source_url && !s.seo_page && <a href={s.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[13.5px] text-slate-500 hover:text-maas">Gegevens fabrikant <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" /></a>}
        </div>
      </div>
    </li>
  );
};

export const ProfileSystemsSection = ({ productKey, productKeys, onderwerp, lead }) => {
  const { pathname } = useLocation();
  const keys = productKeys || (productKey ? [productKey] : []);
  const systems = useProfileSystems().filter((s) => s.product_keys?.some((k) => keys.includes(k)));
  if (!keys.length || !systems.length) return null;
  return (
    <section id="profielsystemen" aria-labelledby="systems-title" className="section scroll-mt-12" data-testid="profile-systems-section">
      <div className="container-tight">
        <SectionHeading id="systems-title" eyebrow="Profielsystemen" title="Beschikbare profielsystemen"
          lead={lead || "Deze profielsystemen kunt u bij ons kiezen. De technische gegevens komen van de fabrikant; de waarden voor uw situatie hangen af van de uitvoering, de maten en het glas."} />
        <ul className={`mt-10 grid gap-6 ${systems.length > 1 ? "md:grid-cols-2" : "max-w-3xl"}`}>
          {systems.map((s) => <SystemCard key={s.slug} s={s} onderwerp={onderwerp} from={pathname} />)}
        </ul>
      </div>
    </section>
  );
};
